import path from 'path';
import fs from 'fs';
import dayjs from 'dayjs';
import { promisify } from 'util';
import { getSyncDeleteCachePath } from '@/common/constant/sync';

/**
 * @description 尝试移除只读属性
 * @param {string} targetPath 目标路径
 */
async function tryRemoveReadonlyAttribute(targetPath: string) {
  try {
    // 尝试修改权限，移除只读属性
    // Windows: 0o666 (rw-rw-rw-), macOS/Linux: 0o755 (rwxr-xr-x)
    const mode = 0o777;
    await promisify(fs.chmod)(targetPath, mode);
    logger.worker('tryRemoveReadonlyAttribute success', targetPath);
    return true;
  } catch (error: any) {
    logger.worker('tryRemoveReadonlyAttribute failed', targetPath, error.message);
    return false;
  }
}

/**
 * @description 检查是否是权限错误
 * @param {any} error 错误对象
 */
function isPermissionError(error: any): boolean {
  return error && ['EPERM', 'EACCES'].includes(error.code);
}

/**
 * @description 移除源路径和目标路径父目录的只读属性
 * @param {string} srcPath 源路径
 * @param {string} dstPath 目标路径
 */
async function tryRemoveParentReadonlyForMove(srcPath: string, dstPath: string) {
  const srcParentDir = path.dirname(srcPath);
  await tryRemoveReadonlyAttribute(srcParentDir);
  const dstParentDir = path.dirname(dstPath);
  if (srcParentDir !== dstParentDir) {
    await tryRemoveReadonlyAttribute(dstParentDir);
  }
}

/**
 * @description 安全执行文件系统操作，自动处理只读权限问题
 * @param {Function} operation 要执行的操作
 * @param {string} targetPath 目标路径
 * @param {string} operationName 操作名称（用于日志）
 */
async function safeFileOperation<T>(
  operation: () => Promise<T>,
  targetPath: string,
  operationName: string,
): Promise<T> {
  try {
    return await operation();
  } catch (error: any) {
    if (isPermissionError(error)) {
      logger.worker(`${operationName} permission denied, try remove readonly`, targetPath);
      await tryRemoveReadonlyAttribute(targetPath);
      return await operation();
    }
    throw error;
  }
}

export default class CoSyncDeleteCache {
  private cacheDir = '';
  private userId = 0;
  private static MAX_TIME_OUT = 1000 * 60 * 60 * 24 * 14;
  private MAX_FOLDER_SIZE = 1024 * 1024 * 1024 * 2;

  setSyncPath(syncPath: string, localSyncId: number) {
    if (!syncPath.length) {
      return;
    }

    this.cacheDir = getSyncDeleteCachePath(syncPath, localSyncId);
    CoSyncDeleteCache.clearTimeOutCache(this.cacheDir);
    this.checkCacheSize();
    logger.worker('CoSyncDeleteCache getSyncDeleteCachePath', this.cacheDir);
  }

  setUserId(userId: number) {
    this.userId = userId;
  }

  static async deleteSyncCache(syncPath: string, localSyncId: number) {
    const cacheDir = getSyncDeleteCachePath(syncPath, localSyncId);
    await CoSyncDeleteCache.deleteCacheByPath(cacheDir);
  }

  async deleteFile(filePath: string) {
    const isExist = await promisify(fs.exists)(filePath);
    if (!isExist) {
      logger.worker('CoSyncDeleteCache deleteFile no filePath', filePath);
      return;
    }

    const newFilePath = await CoSyncDeleteCache.getNewCacheFilePath(filePath, this.cacheDir, this.userId);
    if (!newFilePath.length) {
      logger.worker('CoSyncDeleteCache deleteFile no newFilePath', filePath);
      await safeFileOperation(() => promisify(fs.unlink)(filePath), filePath, 'CoSyncDeleteCache deleteFile');
      return;
    }

    try {
      await promisify(fs.rename)(filePath, newFilePath);
    } catch (error: any) {
      // 如果移动到缓存失败且是权限问题，尝试移除只读属性后重试
      if (isPermissionError(error)) {
        logger.worker(
          'CoSyncDeleteCache deleteFile rename permission denied, try remove readonly',
          filePath,
          newFilePath,
        );
        await tryRemoveParentReadonlyForMove(filePath, newFilePath);
        try {
          await promisify(fs.rename)(filePath, newFilePath);
          logger.worker('CoSyncDeleteCache deleteFile rename success after removing readonly', filePath, newFilePath);
          return;
        } catch (retryError: any) {
          logger.worker('CoSyncDeleteCache deleteFile rename still failed after retry', retryError, filePath);
        }
      }

      // 如果移动失败，fallback 到直接删除
      logger.worker('CoSyncDeleteCache deleteFile renameSync error, fallback to unlink', error, filePath);
      await safeFileOperation(
        () => promisify(fs.unlink)(filePath),
        filePath,
        'CoSyncDeleteCache deleteFile fallback unlink',
      );
    }
  }

  static async clearTimeOutCache(cacheDirPath: string, maxTimeout: number = CoSyncDeleteCache.MAX_TIME_OUT) {
    const isExist = await promisify(fs.exists)(cacheDirPath);
    if (!isExist) {
      logger.worker('CoSyncDeleteCache clearCache no cacheDir', cacheDirPath);
      return false;
    }

    try {
      const fileList = await promisify(fs.readdir)(cacheDirPath);
      for (let i = 0; i < fileList.length; i++) {
        if (this.isFolderTimeOut(fileList[i], maxTimeout)) {
          const dirPath = path.join(cacheDirPath, fileList[i]);
          await this.deleteFolder(dirPath);
        }
      }
      logger.worker('CoSyncDeleteCache clearCache have folder', fileList);
    } catch (error: any) {
      logger.worker('CoSyncDeleteCache clearCache error', error);
    }
    return true;
  }

  async checkCacheSize() {
    const fileSize = await this.getFolderSize(this.cacheDir);
    if (fileSize < this.MAX_FOLDER_SIZE) {
      return false;
    }

    await CoSyncDeleteCache.clearTimeOutCache(this.cacheDir, CoSyncDeleteCache.MAX_TIME_OUT / 3);
    return true;
  }

  async deleteCache() {
    await CoSyncDeleteCache.deleteCacheByPath(this.cacheDir);
  }

  private static async deleteCacheByPath(cacheDirPath: string) {
    try {
      await CoSyncDeleteCache.clearTimeOutCache(cacheDirPath, 0);
      const isExist = await promisify(fs.exists)(cacheDirPath);
      if (isExist) {
        await safeFileOperation(() => promisify(fs.rmdir)(cacheDirPath), cacheDirPath, 'deleteCacheByPath');
        const cacheRootPath = path.dirname(cacheDirPath);
        // 尝试删除根目录，非空会删除失败
        try {
          await promisify(fs.rmdir)(cacheRootPath);
        } catch (error: any) {
          // 根目录删除失败不抛出异常
          logger.worker('deleteCacheByPath root dir delete failed', cacheRootPath, error.message);
        }
      }
    } catch (error) {
      logger.worker('deleteCacheByPath error', cacheDirPath);
    }
  }

  private async getFolderSize(dirPath: string, deep = 0): Promise<number> {
    let fileSize = 0;
    const isExist = await promisify(fs.exists)(dirPath);
    if (!isExist) {
      logger.worker('getFolderSize getFolderSize no exist', dirPath);
      return fileSize;
    }

    const fileList = await promisify(fs.readdir)(dirPath);
    for (let i = 0; i < fileList.length; i++) {
      const subPath = path.join(dirPath, fileList[i]);
      const stats = await promisify(fs.stat)(subPath);
      if (stats.isDirectory() && deep < 10) {
        const size = await this.getFolderSize(subPath, deep + 1);
        fileSize += size;
      } else {
        fileSize += stats.size;
      }
    }

    logger.worker('getFolderSize dirPath', dirPath, deep, fileSize);
    return fileSize;
  }

  private static async deleteFolder(dirPath: string) {
    const stats = await promisify(fs.stat)(dirPath);
    if (stats.isFile()) {
      logger.worker('CoSyncDeleteCache deleteFolder is file', dirPath);
      return;
    }

    const fileList = await promisify(fs.readdir)(dirPath);
    for (let i = 0; i < fileList.length; i++) {
      const filePath = path.join(dirPath, fileList[i]);
      logger.worker('CoSyncDeleteCache deleteFolder deletefile', filePath);
      await safeFileOperation(() => promisify(fs.unlink)(filePath), filePath, 'CoSyncDeleteCache deleteFolder');
    }

    await safeFileOperation(() => promisify(fs.rmdir)(dirPath), dirPath, 'CoSyncDeleteCache deleteFolder');
    logger.worker('CoSyncDeleteCache deleteFolder', dirPath);
  }

  private static isFolderTimeOut(fileName: string, maxTimeout: number) {
    const time = dayjs(fileName).valueOf();
    const curTime = Date.now();
    const span = curTime - time;
    logger.worker('CoSyncDeleteCache isFolderTimeOut', fileName, span > maxTimeout, span, maxTimeout);
    return span > maxTimeout;
  }

  static async getNewCacheFilePath(filePath: string, cacheDir: string, userId: number) {
    if (!cacheDir.length) {
      logger.worker('CoSyncDeleteCache getNewCacheFilePath no cacheDir', filePath);
      return '';
    }

    const nowDate = dayjs().format('YYYY-MM-DD'); // 当前日期，2022-12-22
    const dirPath = path.join(cacheDir, nowDate);
    const ret = await CoSyncDeleteCache.createDir(dirPath);
    if (!ret) {
      logger.worker('getNewCacheFilePath no createDir', dirPath, filePath);
      return '';
    }

    const ext = path.extname(filePath);
    const fileName = path.basename(filePath);
    const now = Date.now();
    const newFilePath = path.join(dirPath, `${fileName}_${userId}_${now}${ext}`);
    logger.worker('CoSyncDeleteCache getNewCacheFilePath', filePath, newFilePath);
    return newFilePath;
  }

  private static async createDir(dirPath: string) {
    const rootDirPath = path.dirname(dirPath);
    let isExist = await promisify(fs.exists)(rootDirPath);
    if (!isExist) {
      const ret = await this.createDir(rootDirPath);
      if (!ret) {
        logger.worker('root createDir fail', rootDirPath);
        return false;
      }
    }

    isExist = await promisify(fs.exists)(dirPath);
    if (!isExist) {
      try {
        await promisify(fs.mkdir)(dirPath);
      } catch (err: any) {
        if (err?.code === 'EEXIST') {
          // 路径已存在，直接返回成功
          logger.worker.error('CoSyncDeleteCache createDir mkdir have exist', err, dirPath);
        } else if (isPermissionError(err)) {
          // 如果创建目录失败且是权限问题，尝试移除父目录只读属性后重试
          await safeFileOperation(
            () => promisify(fs.mkdir)(dirPath),
            path.dirname(dirPath),
            'CoSyncDeleteCache createDir',
          );
        } else {
          logger.worker.error('CoSyncDeleteCache createDir mkdir', err, dirPath);
          throw err;
        }
      }
    }
    logger.worker('createDir', dirPath);
    return true;
  }
}
